import React from 'react';
import { connect } from 'react-redux';
import { toggleValues } from '@department-of-veterans-affairs/platform-site-wide/selectors';
import FEATURE_FLAG_NAMES from '@department-of-veterans-affairs/platform-utilities/featureFlagNames';
import PropTypes from 'prop-types';

import { Toggler } from '~/platform/utilities/feature-toggles';

import {
  claimAvailable,
  isDisabilityCompensationClaim,
  isEightStepPensionClaim,
  withClaimStatusMetaIfEnabled,
} from '~/platform/shared/claims-appeals';
import { clearNotification } from '../actions';
import ClaimDetailLayout from '../components/ClaimDetailLayout';
import ClaimTimeline from '../components/ClaimTimeline';
import ClaimOverviewHeader from '../components/claim-overview-tab/ClaimOverviewHeader';
import DesktopClaimPhaseDiagram from '../components/claim-overview-tab/DesktopClaimPhaseDiagram';
import MobileClaimPhaseDiagram from '../components/claim-overview-tab/MobileClaimPhaseDiagram';

import {
  getStatusMap,
  setPageFocus,
  setTabDocumentTitle,
} from '../utils/helpers';
import { setUpPage, isTab } from '../utils/page';
import ClaimPhaseStepper from '../components/claim-overview-tab/ClaimPhaseStepper';

// HELPERS
const STATUSES = getStatusMap();

const getPhaseFromStatus = latestStatus => {
  if (!latestStatus) return 1;
  return [...STATUSES.keys()].indexOf(latestStatus.toUpperCase()) + 1;
};

const getCustomStepFromStatus = (status, claimStatusMeta) =>
  claimStatusMeta?.overview?.currentStepByStatus?.[status];

class OverviewPage extends React.Component {
  componentDidMount() {
    const { claim } = this.props;
    if (!this.props.asTabPanel) {
      // Only set the document title at mount-time if the claim is already available.
      if (claimAvailable(claim)) setTabDocumentTitle(claim, 'Overview');

      setTimeout(() => {
        const { lastPage, loading } = this.props;
        setPageFocus(lastPage, loading);
      }, 100);
    }
  }

  componentDidUpdate(prevProps) {
    const { claim, lastPage, loading } = this.props;

    if (!this.props.asTabPanel) {
      if (!loading && prevProps.loading && !isTab(lastPage)) {
        setUpPage(false);
      }
      // Set the document title when loading completes.
      //   If loading was successful it will display a title specific to the claim.
      //   Otherwise it will display a default title of "Overview of Your Claim".
      if (loading !== prevProps.loading) {
        setTabDocumentTitle(claim, 'Overview');
      }
    }
  }

  getPageContent() {
    const { claim, cstChampvaCustomContentEnabled } = this.props;
    const displayClaim = withClaimStatusMetaIfEnabled(
      claim,
      cstChampvaCustomContentEnabled,
    );

    // Return null if the claim/ claim.attributes dont exist
    if (!claimAvailable(displayClaim)) {
      return null;
    }

    const {
      claimPhaseDates = {},
      claimDate,
      claimTypeCode,
      claimStatusMeta,
      closeDate,
      status,
      vesStatusUpdatedDate,
    } = displayClaim.attributes;
    const currentPhase =
      getCustomStepFromStatus(status, claimStatusMeta) ||
      getPhaseFromStatus(claimPhaseDates.latestPhaseType);
    const { currentPhaseBack = false } = claimPhaseDates;
    const overviewMeta = claimStatusMeta?.overview || {};
    const overviewSteps = overviewMeta.steps || [];

    if (overviewSteps.length > 0) {
      return (
        <div className="overview-container">
          <ClaimOverviewHeader
            claimTypeCode={claimTypeCode}
            claimStatusMeta={claimStatusMeta}
          />
          <ClaimPhaseStepper
            useVaTabs={this.props.useVaTabs}
            claimDate={claimDate}
            currentClaimPhaseDate={
              // Step 2 = "Application decided" — VES decision date when we have one,
              // otherwise the application's closed date. Same expression ChampvaClosedAlert
              // uses, so the two surfaces cannot show different dates (#155615). We stopped
              // sending vesStatusUpdatedDate in #155448, so closeDate is what renders today.
              // Step 1 uses the internal phaseChangeDate (application receipt timestamp).
              // If steps are ever added, revisit which date each step should show.
              currentPhase === 2
                ? vesStatusUpdatedDate || closeDate
                : claimPhaseDates.phaseChangeDate
            }
            currentPhase={currentPhase || 1}
            currentPhaseBack={currentPhaseBack}
            claimTypeCode={claimTypeCode}
            currentStepPrefix={overviewMeta.currentStepPrefix}
            // Step 2: show the date line when either date is available. Still hidden when
            // both are missing rather than rendering a bare label.
            // Step 1: always show when phaseChangeDate is present.
            showCurrentPhaseDate={
              currentPhase === 2
                ? !!(vesStatusUpdatedDate || closeDate)
                : !!claimPhaseDates.phaseChangeDate
            }
            customSteps={overviewSteps}
          />
        </div>
      );
    }

    return (
      <div className="overview-container">
        <ClaimOverviewHeader
          claimTypeCode={claimTypeCode}
          claimStatusMeta={claimStatusMeta}
        />
        <Toggler toggleName={Toggler.TOGGLE_NAMES.cstClaimPhases}>
          <Toggler.Enabled>
            {isDisabilityCompensationClaim(claimTypeCode) ||
            isEightStepPensionClaim(claimTypeCode) ? (
              <>
                <div className="claim-phase-diagram">
                  <MobileClaimPhaseDiagram currentPhase={currentPhase} />
                  <DesktopClaimPhaseDiagram currentPhase={currentPhase} />
                </div>
                <ClaimPhaseStepper
                  useVaTabs={this.props.useVaTabs}
                  claimDate={claimDate}
                  currentClaimPhaseDate={claimPhaseDates.phaseChangeDate}
                  currentPhase={currentPhase || 1}
                  currentPhaseBack={currentPhaseBack}
                  claimTypeCode={claimTypeCode}
                />
              </>
            ) : (
              <ClaimTimeline
                id={displayClaim.id}
                phase={currentPhase || 1}
                currentPhaseBack={currentPhaseBack}
              />
            )}
          </Toggler.Enabled>
          <Toggler.Disabled>
            <ClaimTimeline
              id={displayClaim.id}
              phase={currentPhase || 1}
              currentPhaseBack={currentPhaseBack}
            />
          </Toggler.Disabled>
        </Toggler>
      </div>
    );
  }

  render() {
    const { claim, cstChampvaCustomContentEnabled, loading, message } =
      this.props;
    const displayClaim = withClaimStatusMetaIfEnabled(
      claim,
      cstChampvaCustomContentEnabled,
    );

    let content = null;
    if (!loading) {
      content = this.getPageContent();
    }

    if (this.props.asTabPanel) {
      return content;
    }

    return (
      <ClaimDetailLayout
        claim={displayClaim}
        loading={loading}
        clearNotification={this.props.clearNotification}
        currentTab="Overview"
        message={message}
      >
        {content}
      </ClaimDetailLayout>
    );
  }
}

function mapStateToProps(state) {
  const claimsState = state.disability.status;

  return {
    loading: claimsState.claimDetail.loading,
    claim: claimsState.claimDetail.detail,
    cstChampvaCustomContentEnabled:
      state.featureToggles?.cst_champva_custom_content || false,
    message: claimsState.notifications.message,
    lastPage: claimsState.routing.lastPage,
    useVaTabs: toggleValues(state)[FEATURE_FLAG_NAMES.cstClaimDetailsVaTabs],
  };
}

const mapDispatchToProps = {
  clearNotification,
};

OverviewPage.propTypes = {
  asTabPanel: PropTypes.bool,
  claim: PropTypes.object,
  clearNotification: PropTypes.func,
  cstChampvaCustomContentEnabled: PropTypes.bool,
  lastPage: PropTypes.string,
  loading: PropTypes.bool,
  message: PropTypes.object,
  params: PropTypes.object,
  useVaTabs: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(OverviewPage);

export { OverviewPage };
